import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Check,
  Eye,
  FolderKanban,
  History,
  Loader2,
  Package,
  Pencil,
  Plus,
  Trash2,
  Wallet,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import {
  useBudget,
  useDeleteBudget,
  useUpdateBudget,
} from "../hooks/useBudgets";
import { useExpenseList } from "@/modules/expenses/hooks/useExpenses";
import { NewExpenseDrawer } from "@/modules/expenses/components/NewExpenseDrawer";
import { BudgetUtilizationBar } from "../components/BudgetUtilizationBar";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";

const BORDER = "hsl(210,30%,85%)";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    draft: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Draft" },
    active: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Active" },
    closed: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "Closed" },
  };
  const s = styles[status] ?? styles.draft;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function ExpenseStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string }> = {
    pending: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)" },
    approved: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
    rejected: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)" },
  };
  const s = styles[status] ?? styles.pending;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
      style={{ background: s.bg, color: s.fg }}
    >
      {status}
    </span>
  );
}

export default function BudgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: budget, isLoading } = useBudget(id ?? "");
  const updateBudget = useUpdateBudget(id ?? "");
  const deleteBudget = useDeleteBudget();

  const { data: expensesResult } = useExpenseList({
    budgetId: id,
    limit: 200,
  });
  const expenses = expensesResult?.data ?? [];

  const [editMode, setEditMode] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [tab, setTab] = useState<"expenses" | "timeline">("expenses");
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editType, setEditType] = useState("project");
  const [editStatus, setEditStatus] = useState("draft");
  const [editTotalAmount, setEditTotalAmount] = useState("");
  const [editCurrency, setEditCurrency] = useState("AED");
  const [editFiscalYear, setEditFiscalYear] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editCompanyId, setEditCompanyId] = useState("");
  const [editProductId, setEditProductId] = useState("");
  const [editProjectId, setEditProjectId] = useState("");
  const [editPhaseId, setEditPhaseId] = useState<string | null>(null);
  const [editStageId, setEditStageId] = useState<string | null>(null);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: editCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: editCompanyId || undefined,
    productId: editProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  function enterEdit() {
    if (!budget) return;
    setEditName(budget.name);
    setEditDescription(budget.description ?? "");
    setEditType(budget.type);
    setEditStatus(budget.status);
    setEditTotalAmount(String(budget.totalAmount));
    setEditCurrency(budget.currency);
    setEditFiscalYear(budget.fiscalYear ? String(budget.fiscalYear) : "");
    setEditStartDate(budget.startDate ? budget.startDate.slice(0, 10) : "");
    setEditEndDate(budget.endDate ? budget.endDate.slice(0, 10) : "");
    setEditCompanyId(budget.companyId ?? "");
    setEditProductId(budget.productId ?? "");
    setEditProjectId(budget.projectId ?? "");
    setEditPhaseId(budget.phaseId ?? null);
    setEditStageId(budget.stageId ?? null);
    setEditMode(true);
  }

  async function saveEdit() {
    if (!budget) return;
    const amt = Number(editTotalAmount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    await updateBudget.mutateAsync({
      name: editName.trim(),
      description: editDescription.trim() || null,
      type: editType,
      status: editStatus,
      totalAmount: amt,
      currency: editCurrency,
      fiscalYear: editFiscalYear ? Number(editFiscalYear) : null,
      startDate: editStartDate ? new Date(editStartDate).toISOString() : null,
      endDate: editEndDate ? new Date(editEndDate).toISOString() : null,
      companyId: editCompanyId || null,
      productId: editProductId || null,
      projectId: editProjectId || null,
      phaseId: editPhaseId,
      stageId: editStageId,
    });
    setEditMode(false);
    showToast("Budget updated");
  }

  function handleDelete() {
    if (!budget) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!budget) return;
    await deleteBudget.mutateAsync(budget.id);
    navigate("/budgets");
  }

  if (isLoading || !budget) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Finance" },
          { label: "Budgets", href: "/budgets" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? (
            <>
              <Loader2 className="mx-auto h-6 w-6 animate-spin" strokeWidth={1.5} />
              <div className="mt-2 text-sm">Loading…</div>
            </>
          ) : (
            "Budget not found"
          )}
        </div>
      </AppLayout>
    );
  }

  const summary = budget.summary;

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Budgets", href: "/budgets" },
        { label: budget.code },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/budgets")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <Wallet className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
          <h1 className="text-xl font-semibold text-foreground">View Budget</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActivityOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="History"
          >
            <History className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => (editMode ? setEditMode(false) : enterEdit())}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Edit"
          >
            <Pencil className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-[hsl(0,70%,45%)]" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* ROW 1 — Summary Header */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: BORDER, padding: "16px 20px" }}
      >
        {editMode ? (
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="text-[20px] font-bold"
          />
        ) : (
          <div className="text-[20px] font-bold text-foreground">
            {budget.name}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
          <ReferenceIdBadge id={budget.code} />
          <StatusPill status={budget.status} />
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
            style={{ background: "hsl(270,55%,92%)", color: "hsl(270,60%,35%)" }}
          >
            {budget.type}
          </span>
          {budget.fiscalYear && <span>FY {budget.fiscalYear}</span>}
          {budget.companyName && (
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3 w-3" strokeWidth={1.5} />
              <Link
                to={`/companies/${budget.companyId}`}
                className="hover:text-[hsl(210,100%,35%)]"
              >
                {budget.companyName}
              </Link>
            </span>
          )}
          {budget.projectName && (
            <span className="inline-flex items-center gap-1">
              <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
              <Link
                to={`/projects/${budget.projectId}`}
                className="hover:text-[hsl(210,100%,35%)]"
              >
                {budget.projectName}
              </Link>
            </span>
          )}
        </div>

        <div className="mt-4">
          <div className="mb-1 text-[11px] uppercase text-muted-foreground">
            Budget Utilization
          </div>
          <BudgetUtilizationBar
            totalAmount={budget.totalAmount}
            usedAmount={budget.usedAmount}
            currency={budget.currency}
            size="lg"
          />
        </div>
      </div>

      {/* ROW 2 — Details + Financial Summary */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-foreground">
              Budget Details
            </h3>
            {editMode ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={saveEdit}
                  className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
                  title="Save"
                >
                  <Check className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                  title="Cancel"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={enterEdit}
                className="flex h-7 w-7 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
                title="Edit"
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Name</div>
              {editMode ? (
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">{budget.name}</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Code</div>
              <div className="mt-0.5 font-mono text-foreground">{budget.code}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Type</div>
              {editMode ? (
                <Select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                >
                  <option value="annual">Annual</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="project">Project</option>
                  <option value="department">Department</option>
                  <option value="emergency">Emergency</option>
                </Select>
              ) : (
                <div className="mt-0.5 text-foreground capitalize">{budget.type}</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Status</div>
              {editMode ? (
                <Select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </Select>
              ) : (
                <div className="mt-1">
                  <StatusPill status={budget.status} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Currency</div>
              {editMode ? (
                <Input
                  value={editCurrency}
                  onChange={(e) => setEditCurrency(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">{budget.currency}</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Fiscal Year
              </div>
              {editMode ? (
                <Input
                  type="number"
                  value={editFiscalYear}
                  onChange={(e) => setEditFiscalYear(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {budget.fiscalYear ?? "—"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">Start Date</div>
              {editMode ? (
                <Input
                  type="date"
                  value={editStartDate}
                  onChange={(e) => setEditStartDate(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {formatDate(budget.startDate)}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">End Date</div>
              {editMode ? (
                <Input
                  type="date"
                  value={editEndDate}
                  onChange={(e) => setEditEndDate(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {formatDate(budget.endDate)}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <div className="text-[11px] uppercase text-muted-foreground">
                Total Amount
              </div>
              {editMode ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editTotalAmount}
                  onChange={(e) => setEditTotalAmount(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-[16px] font-semibold text-foreground">
                  {formatCurrency(budget.totalAmount, budget.currency)}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <div className="text-[11px] uppercase text-muted-foreground">
                Description
              </div>
              {editMode ? (
                <textarea
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="mt-1 flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              ) : (
                <div className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
                  {budget.description ?? "—"}
                </div>
              )}
            </div>
          </div>

          {editMode && (
            <div
              className="mt-4 grid grid-cols-2 gap-4 border-t pt-4 text-sm"
              style={{ borderColor: "hsl(210,30%,90%)" }}
            >
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Company
                </div>
                <Select
                  className="mt-1"
                  value={editCompanyId}
                  onChange={(e) => {
                    setEditCompanyId(e.target.value);
                    setEditProductId("");
                    setEditProjectId("");
                    setEditPhaseId(null);
                    setEditStageId(null);
                  }}
                >
                  <option value="">No company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Product
                </div>
                <Select
                  className="mt-1"
                  value={editProductId}
                  onChange={(e) => {
                    setEditProductId(e.target.value);
                    setEditProjectId("");
                    setEditPhaseId(null);
                    setEditStageId(null);
                  }}
                >
                  <option value="">No product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Project
                </div>
                <Select
                  className="mt-1"
                  value={editProjectId}
                  onChange={(e) => {
                    setEditProjectId(e.target.value);
                    setEditPhaseId(null);
                    setEditStageId(null);
                  }}
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </Select>
              </div>
              {editProjectId && (
                <div className="col-span-2">
                  <PhaseStagePicker
                    projectId={editProjectId || null}
                    phaseId={editPhaseId}
                    stageId={editStageId}
                    onPhaseChange={setEditPhaseId}
                    onStageChange={setEditStageId}
                  />
                </div>
              )}
            </div>
          )}

          <div
            className="mt-4 border-t pt-4 text-[11px] text-muted-foreground"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            Created {formatDateTime(budget.createdAt)}
            {budget.createdByName ? ` by ${budget.createdByName}` : ""}
          </div>
        </div>

        {/* RIGHT — Financial Summary */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Financial Summary
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <StatBox
              label="Total Budget"
              value={formatCurrency(budget.totalAmount, budget.currency)}
              color="hsl(210,100%,28%)"
              bg="hsl(210,55%,94%)"
            />
            <StatBox
              label="Total Spent"
              value={formatCurrency(summary.totalSpent, budget.currency)}
              color="hsl(0,70%,40%)"
              bg="hsl(0,70%,96%)"
            />
            <StatBox
              label="Pending"
              value={formatCurrency(summary.pendingAmount, budget.currency)}
              color="hsl(38,92%,30%)"
              bg="hsl(38,92%,94%)"
            />
            <StatBox
              label="Remaining"
              value={formatCurrency(summary.remainingAmount, budget.currency)}
              color={
                summary.remainingAmount < 0
                  ? "hsl(0,70%,40%)"
                  : "hsl(142,72%,28%)"
              }
              bg={
                summary.remainingAmount < 0
                  ? "hsl(0,70%,96%)"
                  : "hsl(142,55%,94%)"
              }
            />
          </div>

          <div
            className="mt-4 space-y-2 border-t pt-4 text-[12px]"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <div className="text-[11px] uppercase text-muted-foreground">
              Context
            </div>
            {budget.companyId && (
              <Link
                to={`/companies/${budget.companyId}`}
                className="flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
              >
                <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                {budget.companyName}
              </Link>
            )}
            {budget.productId && (
              <Link
                to={`/products/${budget.productId}`}
                className="flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
              >
                <Package className="h-3.5 w-3.5" strokeWidth={1.5} />
                {budget.productName}
              </Link>
            )}
            {budget.projectId && (
              <Link
                to={`/projects/${budget.projectId}`}
                className="flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
              >
                <FolderKanban className="h-3.5 w-3.5" strokeWidth={1.5} />
                {budget.projectName}
              </Link>
            )}
            {!budget.companyId && !budget.productId && !budget.projectId && (
              <div className="text-muted-foreground">No context linked</div>
            )}
          </div>
        </div>
      </div>

      {/* ROW 3 — Tabs */}
      <div
        className="overflow-hidden rounded-xl border bg-white"
        style={{ borderColor: BORDER }}
      >
        <div
          className="flex items-center border-b"
          style={{ borderColor: "hsl(210,30%,90%)" }}
        >
          {[
            { key: "expenses", label: "Expenses", count: summary.totalExpenses },
            { key: "timeline", label: "Timeline", count: 0 },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key as "expenses" | "timeline")}
                className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground"
                }`}
              >
                {t.label}
                <span
                  className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
                  style={{
                    background: active ? "hsl(210,55%,92%)" : "hsl(210,16%,93%)",
                    color: active ? "hsl(210,100%,28%)" : "hsl(215,16%,40%)",
                  }}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {tab === "expenses" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <SummaryStat label="Total" value={String(summary.totalExpenses)} />
                <SummaryStat
                  label="Approved"
                  value={String(summary.approvedExpenses)}
                />
                <SummaryStat
                  label="Pending"
                  value={String(summary.pendingExpenses)}
                />
                <SummaryStat
                  label="Rejected"
                  value={String(summary.rejectedExpenses)}
                />
                <SummaryStat
                  label="Approved Amount"
                  value={formatCurrency(summary.totalSpent, budget.currency)}
                />
              </div>

              <div className="flex items-center justify-end">
                <Button size="sm" onClick={() => setAddExpenseOpen(true)}>
                  <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                  Add Expense
                </Button>
              </div>

              {expenses.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No expenses linked to this budget yet.
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border" style={{ borderColor: BORDER }}>
                  <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
                    <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>#</th>
                        <th className="px-[14px] py-[9px]">Title</th>
                        <th className="px-[14px] py-[9px]" style={{ width: 120 }}>Category</th>
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>Amount</th>
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>Base (AED)</th>
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>Status</th>
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>Date</th>
                        <th className="px-[14px] py-[9px]" style={{ width: 150 }}>Submitted By</th>
                        <th className="px-[14px] py-[9px] text-right" style={{ width: 70 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {expenses.map((e) => (
                        <tr
                          key={e.id}
                          className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                          onClick={() => navigate(`/expenses/${e.id}`)}
                        >
                          <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                            {e.code}
                          </td>
                          <td className="px-[14px] py-[11px] font-medium text-foreground">
                            {e.title}
                          </td>
                          <td className="px-[14px] py-[11px] text-muted-foreground capitalize">
                            {e.category?.replace(/_/g, " ") ?? "—"}
                          </td>
                          <td className="px-[14px] py-[11px]">
                            {formatCurrency(e.amount, e.currency)}
                          </td>
                          <td className="px-[14px] py-[11px]">
                            {formatCurrency(e.baseAmount, e.baseCurrency)}
                          </td>
                          <td className="px-[14px] py-[11px]">
                            <ExpenseStatusBadge status={e.status} />
                          </td>
                          <td className="px-[14px] py-[11px] text-foreground">
                            {formatDate(e.expenseDate)}
                          </td>
                          <td className="px-[14px] py-[11px] text-foreground">
                            {e.submittedByName ?? "—"}
                          </td>
                          <td
                            className="px-[14px] py-[11px] text-right"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-muted"
                              onClick={() => navigate(`/expenses/${e.id}`)}
                              title="View"
                            >
                              <Eye className="h-[15px] w-[15px]" strokeWidth={1.5} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "timeline" && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Budget timeline coming soon.
            </div>
          )}
        </div>
      </div>

      <NewExpenseDrawer
        isOpen={addExpenseOpen}
        onClose={() => setAddExpenseOpen(false)}
        prefill={{
          budgetId: budget.id,
          companyId: budget.companyId,
          productId: budget.productId,
          projectId: budget.projectId,
          locked: { budgetId: true },
        }}
        onCreated={(code) => showToast(`Expense created: ${code}`)}
      />

      <ActivityHistoryDrawer
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        entityType="budget"
        entityId={budget.id}
        entityLabel={budget.name}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete budget?"
        description={`Are you sure you want to delete '${budget.name}'? It will be removed from your Budgets list.`}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}

function StatBox({
  label,
  value,
  color,
  bg,
}: {
  label: string;
  value: string;
  color: string;
  bg: string;
}) {
  return (
    <div className="rounded-lg px-3 py-2.5" style={{ background: bg }}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-[14px] font-semibold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-lg border px-3 py-2"
      style={{ borderColor: "hsl(210,30%,90%)" }}
    >
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-[14px] font-semibold text-foreground">
        {value}
      </div>
    </div>
  );
}
